import { useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { paymentService } from "../services/api";

// ─── Alert ────────────────────────────────────────────────────────────────────
function CustomAlert({ alert, onClose }) {
    if (!alert) return null;

    const cfg = {
        success: {
            iconBg: "bg-emerald-100",
            iconColor: "text-emerald-500",
            btnGradient: "bg-gradient-to-r from-emerald-400 to-emerald-500",
            btnHover: "hover:from-emerald-500 hover:to-emerald-600",
        },
        error: {
            iconBg: "bg-red-100",
            iconColor: "text-red-500",
            btnGradient: "bg-gradient-to-r from-red-400 to-red-500",
            btnHover: "hover:from-red-500 hover:to-red-600",
        },
        warning: {
            iconBg: "bg-amber-100",
            iconColor: "text-amber-500",
            btnGradient: "bg-gradient-to-r from-amber-400 to-yellow-400",
            btnHover: "hover:from-amber-500 hover:to-yellow-500",
        },
        info: {
            iconBg: "bg-cyan-100",
            iconColor: "text-cyan-500",
            btnGradient: "bg-gradient-to-r from-cyan-400 to-cyan-500",
            btnHover: "hover:from-cyan-500 hover:to-cyan-600",
        },
    };

    const icons = {
        success: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <polyline points="20 6 9 17 4 12" />
            </svg>
        ),
        error: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
        ),
        warning: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
        ),
        info: (
            <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5"
            >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
        ),
    };

    const c = cfg[alert.type] || cfg.info;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/10 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, y: 16, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.98 }}
                    transition={{ duration: 0.22, ease: [0.22, 0.68, 0, 1.2] }}
                    className="bg-white rounded-2xl w-full max-w-xs overflow-hidden shadow-xl ring-1 ring-black/5 p-5"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-start gap-3 mb-5">
                        <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.iconBg} ${c.iconColor}`}
                        >
                            {icons[alert.type]}
                        </div>
                        <div className="pt-0.5">
                            <p className="text-sm font-bold text-gray-800 mb-0.5">
                                {alert.title}
                            </p>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                {alert.message}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="flex-1 py-2.5 rounded-xl text-xs font-medium text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-all border border-gray-100"
                        >
                            Tutup
                        </button>
                        <button
                            onClick={onClose}
                            className={`flex-[2] py-2.5 rounded-xl text-xs font-semibold text-white transition-all shadow-sm ${c.btnGradient} ${c.btnHover}`}
                        >
                            {alert.btnText || "Mengerti"}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

// ─── Payment ──────────────────────────────────────────────────────────────────
function Payment() {
    const navigate = useNavigate();
    const location = useLocation();
    // Stores the clinic slug returned by the snap-token API so the
    // onSuccess callback (which runs in Midtrans closure scope) can access it.
    const slugRef = useRef(null);

    const [selectedPlan, setSelectedPlan] = useState(
        sessionStorage.getItem("selectedPlan") || "6bulan",
    );
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [alertData, setAlertData] = useState(null);
    const [formData, setFormData] = useState({
        clinic_name: "",
        address: "",
        email: "",
        password: "",
    });

    const showAlert = (type, title, message, btnText) =>
        setAlertData({ type, title, message, btnText });
    const closeAlert = () => setAlertData(null);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((p) => ({ ...p, [name]: value }));
    };

    const handlePayment = async () => {
        if (!formData.clinic_name || !formData.email || !formData.password) {
            showAlert(
                "warning",
                "Data Belum Lengkap",
                "Mohon lengkapi Nama Klinik, Email, dan Kata Sandi sebelum melanjutkan.",
                "Oke, Lengkapi",
            );
            return;
        }
        if (!window.snap) {
            showAlert(
                "error",
                "Sistem Pembayaran Belum Siap",
                "Midtrans belum dapat dimuat. Mohon muat ulang halaman atau periksa koneksi Anda.",
                "Muat Ulang",
            );
            return;
        }
        setIsLoading(true);
        try {
            const response = await paymentService.getSnapToken({
                plan: selectedPlan,
                ...formData,
            });
            if (response.data?.snap_token) {
                // Note: the Midtrans webhook is async — the slug won't be in
                // the snap-token response. We store the clinic_name so we can
                // generate it client-side as a fallback, but the backend
                // returns the authoritative slug in the webhook notification.
                // For the payment success redirect we store the order_id from
                // this response and pass it as a query param to the success page.
                const orderId = response.data.order_id;

                window.snap.pay(response.data.snap_token, {
                    onSuccess: () => {
                        // Redirect to a generic success page with order_id.
                        // PaymentSuccess page will poll / retrieve the slug.
                        navigate(`/payment-success?order_id=${orderId}&clinic=${encodeURIComponent(formData.clinic_name)}`);
                    },
                    onPending: () =>
                        showAlert(
                            "info",
                            "Menunggu Pembayaran",
                            "Pembayaran sedang diproses. Notifikasi akan dikirim ke email Anda.",
                            "Oke",
                        ),
                    onError: () => {
                        showAlert(
                            "error",
                            "Pembayaran Gagal",
                            "Terjadi kesalahan saat memproses pembayaran. Silakan coba lagi.",
                            "Coba Lagi",
                        );
                        setIsLoading(false);
                    },
                    onClose: () => setIsLoading(false),
                });
            } else {
                showAlert(
                    "error",
                    "Token Tidak Ditemukan",
                    "Gagal mendapatkan token dari server. Silakan coba beberapa saat lagi.",
                    "Coba Lagi",
                );
                setIsLoading(false);
            }
        } catch (err) {
            showAlert(
                "error",
                "Terjadi Kesalahan",
                err.response?.data?.message ||
                    err.message ||
                    "Terjadi kesalahan.",
                "Coba Lagi",
            );
            setIsLoading(false);
        }
    };


    const plans = [
        {
            id: "6bulan",
            label: "6 Bulan",
            price: "Rp 499.000",
            per: "/ 6 bulan",
            badge: null,
            note: "Cocok untuk klinik yang baru memulai digitalisasi antrian.",
        },
        {
            id: "12bulan",
            label: "12 Bulan",
            price: "Rp 899.000",
            per: "/ 12 bulan",
            badge: "Hemat 10%",
            note: "Nilai terbaik untuk klinik yang ingin jangka panjang.",
        },
    ];

    const active = plans.find((p) => p.id === selectedPlan);

    const inputBase =
        "w-full px-4 py-3 rounded-xl text-sm text-gray-800 placeholder:text-gray-300 outline-none transition-all duration-200 bg-gray-50/80 focus:bg-white";

    const inputStyle = { border: "1.5px solid #e2edef" };

    const onFocus = (e) => {
        e.target.style.borderColor = "#00B8DB";
        e.target.style.boxShadow = "0 0 0 3px rgba(8,145,178,0.11)";
    };
    const onBlur = (e) => {
        e.target.style.borderColor = "#e2edef";
        e.target.style.boxShadow = "none";
    };

    const fadeUp = (delay = 0) => ({
        initial: { opacity: 0, y: 16 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.45, delay, ease: [0.25, 0.46, 0.45, 0.94] },
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-white via-cyan-50/30 to-sky-50/50 font-body selection:bg-cyan-100 selection:text-cyan-700">
            <CustomAlert alert={alertData} onClose={closeAlert} />

            <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-100/40 blur-[120px]" />
                <div className="absolute bottom-0 -left-20 w-[400px] h-[400px] rounded-full bg-sky-100/30 blur-[100px]" />
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                {/* Header */}
                <motion.div {...fadeUp(0)} className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-display font-semibold text-gray-900 leading-tight tracking-tight mb-3">
                        Mulai Berlangganan
                    </h1>
                    <p className="text-gray-400 text-base max-w-md mx-auto">
                        Lengkapi data klinik dan pilih paket yang sesuai
                        kebutuhan Anda.
                    </p>
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-6 items-stretch">
                    {/* ── Left: Form ── */}
                    <motion.div
                        {...fadeUp(0.1)}
                        className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100 shadow-sm p-8 flex flex-col"
                    >
                        <h2 className="text-base font-semibold text-gray-700 mb-6 flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-cyan-500 text-white text-xs flex items-center justify-center font-bold">
                                1
                            </span>
                            Informasi Klinik
                        </h2>

                        <div className="flex flex-col gap-5 flex-1">
                            {/* Nama Klinik */}
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                                    Nama Klinik
                                </label>
                                <input
                                    type="text"
                                    name="clinic_name"
                                    value={formData.clinic_name}
                                    onChange={handleChange}
                                    placeholder="Klinik Sehat Bersama"
                                    className={inputBase}
                                    style={inputStyle}
                                    onFocus={onFocus}
                                    onBlur={onBlur}
                                />
                            </div>

                            {/* Alamat */}
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                                    Alamat Klinik
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    placeholder="Jl. Contoh No. 123, Jakarta"
                                    className={inputBase}
                                    style={inputStyle}
                                    onFocus={onFocus}
                                    onBlur={onBlur}
                                />
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="klinik@email.com"
                                    className={inputBase}
                                    style={inputStyle}
                                    onFocus={onFocus}
                                    onBlur={onBlur}
                                />
                            </div>

                            {/* Password */}
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                                    Kata Sandi
                                </label>
                                <div className="relative">
                                    <input
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="Min. 8 karakter"
                                        className={`${inputBase} pr-11`}
                                        style={inputStyle}
                                        onFocus={onFocus}
                                        onBlur={onBlur}
                                    />
                                    <button
                                        onClick={() =>
                                            setShowPassword(!showPassword)
                                        }
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-[#0891b2] transition-colors"
                                    >
                                        {showPassword ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                                <line
                                                    x1="1"
                                                    y1="1"
                                                    x2="23"
                                                    y2="23"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                                <circle cx="12" cy="12" r="3" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                                <p className="mt-2 text-[11px] text-gray-400 flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-cyan-400 inline-block flex-shrink-0" />
                                    Kombinasi huruf besar, kecil, dan angka
                                </p>
                            </div>

                            {/* Security note */}
                            <div className="mt-auto rounded-2xl bg-cyan-50 border border-gray-100 px-4 py-3.5 flex items-center gap-3">
                                <div className="w-7 h-7 rounded-lg bg-cyan-100 border border-gray-200 shadow-sm flex items-center justify-center flex-shrink-0">
                                    <svg
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.75"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="w-3.5 h-3.5 text-cyan-400"
                                    >
                                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                    </svg>
                                </div>
                                <p className="text-[11px] text-gray-400 leading-relaxed">
                                    Data Anda dienkripsi dan{" "}
                                    <span className="text-gray-600 font-medium">
                                        tidak dibagikan
                                    </span>{" "}
                                    kepada pihak ketiga.
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* ── Right: Plans + Summary + CTA ── */}
                    <motion.div
                        {...fadeUp(0.15)}
                        className="flex flex-col gap-4"
                    >
                        <div className="bg-white/70 backdrop-blur-sm rounded-3xl border border-gray-100 shadow-sm p-8 flex flex-col flex-1">
                            <h2 className="text-base font-semibold text-gray-700 mb-5 flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-cyan-500 text-white text-xs flex items-center justify-center font-bold">
                                    2
                                </span>
                                Pilih Paket
                            </h2>

                            <div className="flex flex-col gap-3">
                                {plans.map((plan) => {
                                    const on = selectedPlan === plan.id;
                                    return (
                                        <div
                                            key={plan.id}
                                            onClick={() =>
                                                setSelectedPlan(plan.id)
                                            }
                                            className={`relative cursor-pointer rounded-2xl border-2 px-5 py-5 transition-all duration-200 ${on ? "border-cyan-300 bg-cyan-50/50" : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/40"}`}
                                        >
                                            {plan.badge && (
                                                <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full bg-cyan-500 text-white text-[10px] font-bold tracking-wide shadow-sm shadow-cyan-200">
                                                    {plan.badge}
                                                </span>
                                            )}
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start gap-3 min-w-0">
                                                    <div
                                                        className={`mt-1 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${on ? "border-cyan-500" : "border-gray-200"}`}
                                                    >
                                                        {on && (
                                                            <div className="w-2 h-2 rounded-full bg-cyan-500" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p
                                                            className={`text-sm font-semibold mb-1.5 ${on ? "text-gray-900" : "text-gray-600"}`}
                                                        >
                                                            {plan.label}
                                                        </p>
                                                        <p className="text-[11px] text-gray-400 leading-relaxed">
                                                            {plan.note}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p
                                                        className={`text-base font-bold leading-none mb-1.5 ${on ? "text-cyan-500" : "text-gray-400"}`}
                                                    >
                                                        {plan.price}
                                                    </p>
                                                    <p className="text-[10px] text-gray-400">
                                                        {plan.per}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Summary */}
                            <div className="mt-auto pt-6">
                                <div className="h-px bg-gray-100 mb-5" />
                                <div className="flex items-end justify-between">
                                    <div>
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
                                            Total Pembayaran
                                        </p>
                                        <p className="text-2xl font-bold text-gray-900 leading-none">
                                            {active?.price}
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
                                            Durasi
                                        </p>
                                        <p className="text-sm font-semibold text-gray-700">
                                            {active?.label}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CTA */}
                        <button
                            onClick={handlePayment}
                            disabled={isLoading}
                            className={`w-full py-4 rounded-2xl bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white text-sm font-semibold transition-all duration-200 shadow-md shadow-cyan-200/60 hover:shadow-cyan-300/60 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 ${isLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                        >
                            {isLoading ? (
                                <>
                                    <svg
                                        className="animate-spin w-4 h-4"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                    >
                                        <path
                                            d="M21 12a9 9 0 1 1-6.219-8.56"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    Memproses...
                                </>
                            ) : (
                                <>
                                    Berlangganan Sekarang{" "}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="15"
                                        height="15"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M5 12h14" />
                                        <path d="m12 5 7 7-7 7" />
                                    </svg>
                                </>
                            )}
                        </button>

                        <p className="text-[11px] text-gray-400 text-center">
                            Dengan melanjutkan, Anda menyetujui{" "}
                            <span className="text-cyan-500 font-medium cursor-pointer hover:underline">
                                Syarat & Ketentuan
                            </span>{" "}
                            yang berlaku.
                        </p>
                    </motion.div>
                </div>
            </div>
        </div>
    );
}

export default Payment;